import { useMemo } from "react";
import { fileParserService } from "@/services/data";
import { useBookmarksContext } from "@/stores/bookmark";
import type { DataflowData } from "@/types/stores/dataflow";
import { useUserFiles } from "@/stores/user-file";
import { getInputDataName } from "@/utils/data/getInputDataName";
import { useTranslation } from "@/hooks";

interface VisualData {
  data: any[];
  metadata: { columns: string[] };
}

interface DatasetInfo {
  name: string;
  format: string;
  rowCount: number;
  columnCount: number;
  isGeocoded: boolean;
}

export function useDatasetInfo(
  activeDataflow: DataflowData | undefined,
  visualData: VisualData | null
): DatasetInfo | null {
  const userFiles = useUserFiles();
  const { getBookmarkFromTableId } = useBookmarksContext();
  const { currentLanguage } = useTranslation();

  return useMemo(() => {
    if (!activeDataflow || !visualData) return null;

    const inputTransformer = activeDataflow.flow_definition_json?.find(
      (t) => t.type === "user_file" || t.type === "data_input"
    );

    if (!inputTransformer) return null;

    const name = getInputDataName(
      inputTransformer,
      userFiles,
      getBookmarkFromTableId,
      currentLanguage
    );

    let format = "";
    if (inputTransformer.type === "user_file") {
      const userFile = userFiles.find((f) => f.id === inputTransformer.file_id);
      format = userFile?.type || "Unknown";
    } else if (inputTransformer.type === "data_input") {
      format = "Dataset";
    }

    const isGeocoded = visualData.metadata.columns
      ? fileParserService.hasLatLonColumns(visualData.metadata.columns).has
      : false;

    return {
      name: name || "",
      format,
      rowCount: visualData.data.length,
      columnCount: visualData.metadata.columns.length,
      isGeocoded,
    };
  }, [
    activeDataflow,
    visualData,
    userFiles,
    getBookmarkFromTableId,
    currentLanguage,
  ]);
}

